import { Converter } from '../types/converter';

export const weightAndMassConverter: Converter = {
  id: 'weightAndMass',
  name: 'Weight and Mass Converter',
  icon: 'Weight',
  categories: ['common', 'engineering'],
  units: [
    // SI units and their decimal multiples/submultiples
    { name: 'Kilogram', symbol: 'kg', factor: 1 },
    { name: 'Gram', symbol: 'g', factor: 0.001 },
    { name: 'Milligram', symbol: 'mg', factor: 1e-6 },
    { name: 'Metric Ton', symbol: 'tonne', factor: 1000 },
    { name: 'Pound', symbol: 'lbs', factor: 0.45359237 },
    { name: 'Ounce', symbol: 'oz', factor: 0.028349523125 },
    { name: 'Carat', symbol: 'ct', factor: 0.0002 },
    { name: 'Short Ton', symbol: 'ton (US)', factor: 907.18474 },
    { name: 'Long Ton', symbol: 'ton (UK)', factor: 1016.047 },
    { name: 'Atomic Mass Unit', symbol: 'u', factor: 1.660539067e-27 },
    { name: 'Exagram', symbol: 'Eg', factor: 1e15 },
    { name: 'Petagram', symbol: 'Pg', factor: 1e12 },
    { name: 'Teragram', symbol: 'Tg', factor: 1e9 },
    { name: 'Gigagram', symbol: 'Gg', factor: 1e6 },
    { name: 'Megagram', symbol: 'Mg', factor: 1000 },
    { name: 'Hectogram', symbol: 'hg', factor: 0.1 },
    { name: 'Dekagram', symbol: 'dag', factor: 0.01 },
    { name: 'Decigram', symbol: 'dg', factor: 0.0001 },
    { name: 'Centigram', symbol: 'cg', factor: 0.00001 },
    { name: 'Microgram', symbol: 'µg', factor: 1e-9 },
    { name: 'Nanogram', symbol: 'ng', factor: 1e-12 },
    { name: 'Picogram', symbol: 'pg', factor: 1e-15 },
    { name: 'Femtogram', symbol: 'fg', factor: 1e-18 },
    { name: 'Attogram', symbol: 'ag', factor: 1e-21 },
    { name: 'Dalton', symbol: 'Da', factor: 1.660539067e-27 },
    { name: 'Kilogram-force square second/meter', symbol: 'kgf·s²/m', factor: 9.80665 },
    { name: 'Kilopound', symbol: 'kip', factor: 453.59237 },
    { name: 'Slug', symbol: 'slug', factor: 14.593903 },
    { name: 'Pound-force square second/foot', symbol: 'lbf·s²/ft', factor: 14.593903 },
    { name: 'Troy Pound', symbol: 'lb t', factor: 0.3732417216 },
    { name: 'Poundal', symbol: 'pdl', factor: 0.0140867195 },
    { name: 'Assay Ton (US)', symbol: 'AT (US)', factor: 0.02916667 },
    { name: 'Assay Ton (UK)', symbol: 'AT (UK)', factor: 0.032666667 },
    { name: 'Metric Kiloton', symbol: 'kt', factor: 1e6 },
    { name: 'Quintal', symbol: 'cwt', factor: 100 },
    { name: 'US Hundredweight', symbol: 'cwt (US)', factor: 45.359237 },
    { name: 'UK Hundredweight', symbol: 'cwt (UK)', factor: 50.802345 },
    { name: 'Quarter (US)', symbol: 'qr (US)', factor: 11.33980925 },
    { name: 'Quarter (UK)', symbol: 'qr (UK)', factor: 12.70058625 },
    { name: 'Stone (US)', symbol: 'st (US)', factor: 5.669904625 },
    { name: 'Stone (UK)', symbol: 'st (UK)', factor: 6.35029318 },
    { name: 'Pennyweight', symbol: 'dwt', factor: 0.001555173962 },
    { name: 'Scruple', symbol: 's.ap', factor: 0.001295978636 },
    { name: 'Grain', symbol: 'gr', factor: 0.00006479891821 },
    { name: 'Gamma', symbol: 'γ', factor: 1e-9 },
    
    // Biblical units
    { name: 'Talent (Biblical Hebrew)', symbol: 'talent-heb', factor: 34.2 },
    { name: 'Mina (Biblical Hebrew)', symbol: 'mina-heb', factor: 0.57 },
    { name: 'Shekel (Biblical Hebrew)', symbol: 'shekel-heb', factor: 0.0114 },
    { name: 'Bekan (Biblical Hebrew)', symbol: 'bekan-heb', factor: 0.0057 },
    { name: 'Gerah (Biblical Hebrew)', symbol: 'gerah-heb', factor: 0.00057 },
    { name: 'Talent (Biblical Greek)', symbol: 'talent-gr', factor: 20.4 },
    { name: 'Mina (Biblical Greek)', symbol: 'mina-gr', factor: 0.34 },
    { name: 'Tetradrachma (Biblical Greek)', symbol: 'tetradrachma', factor: 0.014 },
    { name: 'Didrachma (Biblical Greek)', symbol: 'didrachma', factor: 0.007 },
    { name: 'Drachma (Biblical Greek)', symbol: 'drachma', factor: 0.0035 },
    { name: 'Denarius (Biblical Roman)', symbol: 'denarius', factor: 0.0035 },
    { name: 'Assarion (Biblical Roman)', symbol: 'assarion', factor: 0.00035 },
    { name: 'Quadrans (Biblical Roman)', symbol: 'quadrans', factor: 0.000087 },
    { name: 'Lepton (Biblical Roman)', symbol: 'lepton', factor: 0.000044 },
    
    // Physical constant
    { name: 'Planck Mass', symbol: 'mP', factor: 2.176434e-8 },
    { name: 'Electron Mass', symbol: 'me', factor: 9.1093837015e-31 },
    { name: 'Muon Mass', symbol: 'mμ', factor: 1.883531627e-28 },
    { name: 'Proton Mass', symbol: 'mp', factor: 1.67262192369e-27 },
    { name: 'Neutron Mass', symbol: 'mn', factor: 1.67492749804e-27 },
    { name: 'Deuteron Mass', symbol: 'md', factor: 3.3435837724e-27 },
    
    // Astronomical masses
    { name: "Earth's Mass", symbol: 'M⊕', factor: 5.9722e24 },
    { name: "Sun's Mass", symbol: 'M☉', factor: 1.98847e30 }
  ]
};
